import { getFirestore, doc, getDoc, updateDoc, collection, addDoc, Timestamp } from 'firebase/firestore'
import { useCatalogsStore } from '@/stores/catalogs'
import { useCompanyStore } from '@/stores/company'
import { useAuthStore } from '@/stores/auth'
import { usePOChangeLog } from './usePOChangeLog'
import { allStatusSetStatuses, resolveStatusList } from '@/utils/shipmentStatusSets'

export type TemplateTriggerModule = 'purchaseOrders' | 'shipments' | 'containers'

interface StatusConfig {
  id: string
  label: string
  trigger?: string
  triggerType?: 'field' | 'template' | 'manual' | 'creation'
  templateTrigger?: string
  type: string
  order: number
  visible?: boolean
}

/**
 * Composable para gestionar triggers de plantillas de correo sobre estados de módulos.
 * Cuando se envía un correo con una plantilla configurada como trigger de un estado,
 * este composable actualiza el estado del documento (OC o Embarque) automáticamente.
 */
export function useTemplateTrigger() {
  const catalogsStore = useCatalogsStore()
  const companyStore = useCompanyStore()
  const authStore = useAuthStore()
  const db = getFirestore()
  const { logChange } = usePOChangeLog()

  /**
   * Lee los estados directamente de Firestore (sin depender del store en memoria)
   */
  const fetchStatusConfigFromFirestore = async (
    companyId: string,
    module: TemplateTriggerModule
  ): Promise<StatusConfig[]> => {
    const configKey = module === 'purchaseOrders' ? 'PURCHASE_ORDER_STATUS' : 'SHIPMENT_STATUS'
    const configRef = doc(db, `Companies/${companyId}/config/${configKey}`)
    const snap = await getDoc(configRef)

    if (!snap.exists()) return []

    const data = snap.data()
    let statuses: StatusConfig[] = []

    if (data?.statusByMode) {
      const seen = new Set<string>()
      Object.values(data.statusByMode as Record<string, StatusConfig[]>).forEach((modeStatuses) => {
        if (Array.isArray(modeStatuses)) {
          modeStatuses.forEach((s) => {
            if (!seen.has(s.id)) {
              seen.add(s.id)
              statuses.push(s)
            }
          })
        }
      })
    } else if (data?.status && Array.isArray(data.status)) {
      statuses = data.status
    } else if (data?.statusByContainer && module === 'containers') {
      statuses = data.statusByContainer
    }

    if (module === 'shipments') {
      const ids = new Set(statuses.map(s => s.id))
      for (const s of allStatusSetStatuses(data) as unknown as StatusConfig[]) {
        if (!ids.has(s.id)) { ids.add(s.id); statuses.push(s) }
      }
    }

    return statuses
  }

  /**
   * Obtiene todos los estados de un módulo que tienen trigger de tipo "template".
   * Intenta leer del store en memoria primero; si no está cargado, lee de Firestore.
   */
  const getStatusesWithTemplateTrigger = (module: TemplateTriggerModule): StatusConfig[] => {
    let statuses: StatusConfig[] = []

    if (module === 'purchaseOrders') {
      const config = (catalogsStore.configs as any)?.PURCHASE_ORDER_STATUS
      if (config?.status && Array.isArray(config.status)) {
        statuses = config.status
      }
    } else if (module === 'shipments') {
      const config = (catalogsStore.configs as any)?.SHIPMENT_STATUS
      if (config?.statusByMode) {
        const seen = new Set<string>()
        Object.values(config.statusByMode as Record<string, StatusConfig[]>).forEach((modeStatuses) => {
          if (Array.isArray(modeStatuses)) {
            modeStatuses.forEach((s) => {
              if (!seen.has(s.id)) {
                seen.add(s.id)
                statuses.push(s)
              }
            })
          }
        })
      } else if (config?.status && Array.isArray(config.status)) {
        statuses = [...config.status]
      }
      const ids = new Set(statuses.map(s => s.id))
      for (const s of allStatusSetStatuses(config) as unknown as StatusConfig[]) {
        if (!ids.has(s.id)) { ids.add(s.id); statuses.push(s) }
      }
    } else if (module === 'containers') {
      const config = (catalogsStore.configs as any)?.SHIPMENT_STATUS
      if (config?.statusByContainer && Array.isArray(config.statusByContainer)) {
        statuses = config.statusByContainer
      }
    }

    return statuses.filter(s => s.triggerType === 'template' && s.templateTrigger)
  }

  /**
   * Busca si hay un estado configurado con la plantilla dada como trigger
   */
  const findStatusByTemplate = (
    templateId: string,
    module: TemplateTriggerModule
  ): StatusConfig | null => {
    const statuses = getStatusesWithTemplateTrigger(module)
    return statuses.find(s => s.templateTrigger === templateId) || null
  }

  /**
   * Auto-llena un campo de fecha si la plantilla lo tiene configurado
   * y el campo aún no tiene valor (solo primera vez)
   */
  const applyAutoFillDateField = async (
    template: any,
    templateContext: any
  ): Promise<void> => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId || !template?.autoFillDateField || !templateContext) {
      return
    }

    const { autoFillDateField } = template
    
    // Usar el módulo definido en autoFillDateField, no el del contexto de envío
    const targetModule = autoFillDateField.module
    const targetEntityId = templateContext[targetModule]?.id
    
    
    if (!targetEntityId) {
      console.warn('[AutoFillDate] No se encontró entityId para el módulo:', { 
        targetModule,
        availableModules: Object.keys(templateContext)
      })
      return
    }

    try {
      let collectionPath: string
      if (targetModule === 'purchaseOrders') {
        collectionPath = `Companies/${companyId}/purchaseOrders`
      } else if (targetModule === 'containers') {
        collectionPath = `Companies/${companyId}/containers`
      } else {
        collectionPath = `Companies/${companyId}/shipments`
      }

      const entityRef = doc(db, collectionPath, targetEntityId)
      const entitySnap = await getDoc(entityRef)

      if (!entitySnap.exists()) {
        console.warn('[AutoFillDate] Documento no encontrado')
        return
      }

      const entityData = entitySnap.data()
      const { fieldId } = autoFillDateField


      // Verificar si el campo ya tiene valor
      let currentValue: any = null
      if (fieldId.includes('.')) {
        // Campo anidado (ej: 'milestones.firstNotificationDate')
        const parts = fieldId.split('.')
        let temp: any = entityData
        for (const part of parts) {
          temp = temp?.[part]
          if (temp === undefined) break
        }
        currentValue = temp
      } else {
        // Campo raíz
        currentValue = entityData[fieldId]
      }


      // Solo llenar si el campo está vacío (primera vez)
      // Considerar null, undefined, y strings vacías como "vacío"
      if (currentValue !== null && currentValue !== undefined && currentValue !== '') {
        return
      }

      // Generar timestamp en formato ISO string
      const now = new Date()
      const isoString = now.toISOString().split('.')[0] // YYYY-MM-DDTHH:mm:ss


      // Actualizar el campo
      const updateData: any = {}
      updateData[fieldId] = isoString
      updateData.updatedAt = Timestamp.now()


      await updateDoc(entityRef, updateData)


      // Registrar en el historial
      const user = authStore.user
      const changesRef = collection(db, `${collectionPath}/${targetEntityId}/changeLogs`)
      await addDoc(changesRef, {
        action: 'FIELD_AUTO_FILLED',
        description: `Campo "${autoFillDateField.fieldLabel}" auto-llenado por envío de plantilla "${template.name}"`,
        timestamp: Timestamp.now(),
        userId: user?.uid || 'SYSTEM',
        userName: user?.name
          ? `${user.name}${user.lastName ? ' ' + user.lastName : ''}`.trim()
          : user?.email || 'Sistema',
        changes: [{ 
          field: fieldId, 
          fieldLabel: autoFillDateField.fieldLabel, 
          oldValue: null, 
          newValue: isoString 
        }],
        metadata: { 
          triggeredByTemplate: template.id, 
          templateName: template.name,
          autoFill: true 
        }
      })

    } catch (err) {
      console.error('❌ [AutoFillDate] Error al auto-llenar campo de fecha:', err)
      console.error('[AutoFillDate] Detalles del error:', {
        template: template?.name,
        fieldId: autoFillDateField?.fieldId,
        targetModule,
        targetEntityId,
        error: err
      })
    }
  }

  /**
   * Aplica el trigger de una plantilla sobre una OC o Embarque.
   * Si hay un estado configurado con esa plantilla como trigger, actualiza el status
   * del documento y registra el evento en el historial de cambios.
   *
   * Se puede llamar una vez por cada entidad del contexto: si la plantilla no es
   * trigger de ningún estado de ese módulo, la función sale sin escribir nada.
   * El auto-llenado de fecha NO va acá: define su propio módulo destino, así que
   * lo invoca el caller una sola vez (`applyAutoFillDateField`).
   *
   * @param templateId   ID de la plantilla enviada
   * @param templateName Nombre legible de la plantilla (para el log)
   * @param module       Módulo destino: 'purchaseOrders' | 'shipments' | 'containers'
   * @param entityId     ID del documento a actualizar
   */
  const applyTemplateTrigger = async (
    templateId: string,
    templateName: string,
    module: TemplateTriggerModule,
    entityId: string
  ): Promise<void> => {
    const companyId = companyStore.selectedCompanyId
    if (!companyId || !entityId || !templateId) {
      console.warn('[TemplateTrigger] Faltan datos:', { companyId, entityId, templateId })
      return
    }

    // Leer config directamente de Firestore para garantizar datos actualizados
    let allStatuses: StatusConfig[]
    if (module === 'shipments') {
      // Solo la lista que le corresponde al embarque (conjunto por campo o modo): un trigger de
      // un estado de otra lista no debe aplicarse, y el de un conjunto no se pierde por id repetido.
      const configSnap = await getDoc(doc(db, `Companies/${companyId}/config/SHIPMENT_STATUS`))
      const shipmentSnap = await getDoc(doc(db, `Companies/${companyId}/shipments`, entityId))
      if (!configSnap.exists() || !shipmentSnap.exists()) return
      allStatuses = (resolveStatusList<StatusConfig>(configSnap.data(), shipmentSnap.data())?.statuses ?? [])
    } else {
      allStatuses = await fetchStatusConfigFromFirestore(companyId, module)
    }
    const allStatusesWithTrigger = allStatuses.filter(s => s.triggerType === 'template' && s.templateTrigger)

    allStatusesWithTrigger.forEach(s => {
    })

    const targetStatus = allStatusesWithTrigger.find(s => s.templateTrigger === templateId) || null
    if (!targetStatus) return

    try {
      let collectionPath: string
      if (module === 'purchaseOrders') {
        collectionPath = `Companies/${companyId}/purchaseOrders`
      } else if (module === 'containers') {
        collectionPath = `Companies/${companyId}/containers`
      } else {
        collectionPath = `Companies/${companyId}/shipments`
      }

      const entityRef = doc(db, collectionPath, entityId)
      await updateDoc(entityRef, {
        status: targetStatus.id,
        updatedAt: Timestamp.now()
      })

      if (module === 'purchaseOrders') {
        await logChange(
          entityId,
          'STATUS_CHANGED',
          `Estado cambiado a "${targetStatus.label}" por envío de plantilla "${templateName}"`,
          [{ field: 'status', fieldLabel: 'Estado', oldValue: null, newValue: targetStatus.id }],
          { triggeredByTemplate: templateId, templateName }
        )
      } else {
        const user = authStore.user
        const changesRef = collection(db, `${collectionPath}/${entityId}/changeLogs`)
        await addDoc(changesRef, {
          action: 'STATUS_CHANGED',
          description: `Estado cambiado a "${targetStatus.label}" por envío de plantilla "${templateName}"`,
          timestamp: Timestamp.now(),
          userId: user?.uid || 'SYSTEM',
          userName: user?.name
            ? `${user.name}${user.lastName ? ' ' + user.lastName : ''}`.trim()
            : user?.email || 'Sistema',
          changes: [{ field: 'status', fieldLabel: 'Estado', oldValue: null, newValue: targetStatus.id }],
          metadata: { triggeredByTemplate: templateId, templateName }
        })
      }

    } catch (err) {
      console.error('❌ Error al aplicar trigger de plantilla:', err)
    }
  }

  return {
    findStatusByTemplate,
    getStatusesWithTemplateTrigger,
    applyTemplateTrigger,
    applyAutoFillDateField
  }
}
